/**
 * COMPTABILITÉ — LE DÉTAIL D'UN POINT DE VENTE, DANS LA CLÔTURE.
 *
 * Sous les montants à rapprocher, ce que la nuit a vraiment été à ce comptoir :
 * caisse par caisse, ce qui s'est vendu, à quelle heure, le panier moyen,
 * l'heure de pointe ; les doses servies par la rampe du bar et les bouteilles
 * changées ; les ouvertures de tiroir ; les offerts ; et, à l'accueil, le
 * guichet Shotgun par tarif et par heure.
 *
 * ═══ À LIRE SI L'ON VEUT ═══
 * Rien ici ne change la clôture : c'est la lecture des annexes ZAP et du
 * guichet, servie par `GET /finance/nights/:date/pos/:id/detail`. Le serveur
 * décide ce que la session peut voir — offerts et guichet partent avec leurs
 * propres droits et arrivent à `null` sans eux : l'écran le dit au lieu
 * d'afficher zéro.
 *
 * ═══ CE QUE L'ÉCRAN S'INTERDIT ═══
 *   · un montant inconnu s'écrit « — », jamais 0 ;
 *   · aucun texte coupé : les noms longs reviennent à la ligne ;
 *   · le libellé de caisse du rapport reste au SECOND rang, sous le nom de
 *     Gestion ;
 *   · la vente au guichet est une VALEUR FACIALE, et se dit ainsi.
 *
 * Le graphique d'affluence est maison (barres CSS, aucune bibliothèque) : une
 * seule série à la fois, une barre par heure de la nuit — heures creuses
 * comprises, pour qu'un creux se lise comme un creux —, survol ET focus
 * clavier, et sa vue « tableau » pour qui ne lit pas un graphique.
 *
 * ═══ LA LARGEUR SERT ═══
 * Le Chrome de l'exploitant fait 2 560 px : le détail ne s'empile plus. Les
 * caisses sont côte à côte (une colonne par caisse quand elle tient), leurs
 * rampes aussi, alignées sous elles ; l'affluence, les tiroirs, les offerts et
 * les produits se rangent en deux ou trois colonnes selon la largeur de la
 * SECTION (requêtes de conteneur, voir finance-pos.css). Les produits montrent
 * les quinze premiers ; les autres se déplient d'un clic ou d'une recherche.
 *
 * ═══ QUAND DES TICKETS MANQUENT ═══
 * Le détail disait « relancez sa lecture » à quelqu'un qui n'avait aucun moyen
 * de le faire. C'est désormais un bouton, « Lire les tickets de cette nuit »
 * (`POST /finance/nights/:date/annexes/reread`, droit
 * `finance.source.manage`) : il relit les détails de caisse de la nuit par le
 * pipeline normal, ne touche à aucune clôture, et rend un bilan qui reste
 * affiché après le rechargement.
 */

import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import {
  AlertTriangle, Banknote, BarChart3, CheckCircle2, Clock, CreditCard, GlassWater, Gift, Package, Receipt, RefreshCw, Search, Ticket, Vault,
} from 'lucide-react'
import { adminMutate } from '../admin/primitives.js'
import { SegmentedControl } from '../admin/pickers.js'
import { formatMoney } from './money.js'
import { EnCours, Indisponible, heureParis, lireJson, type Etat } from './ClosingCommon.js'
import { VoirSource, type DocVisible } from './SourceViewer.js'
import './finance-pos.css'

/* ═══════════════════════ les formes lues (miroir du cœur, `pos-detail.ts`) ═══════════════════════ */

interface Heure { hour_start: string; label: string; count: number; amount_cents: number; gross_cents: number | null }

interface Produit {
  product: string; qty: number; gross_cents: number | null; revenue_cents: number | null
  non_revenue_cents: number | null; unit_prices_cents: number[]; dosed_qty: number
}

interface Offert { at: string | null; holder: string; paid_cents: number; discount_cents: number; articles: Array<{ qty: number; product: string; cents: number | null }> }

interface Caisse {
  register_id: string
  register_name: string
  zap_labels: string[]
  sessions: Array<{
    document_id: string; filename: string; session_no: number | null; offline: boolean
    opened_at: string | null; closed_at: string | null; tickets: number | null; problem: string | null; warnings: string[]
  }>
  by_method: Array<{ label: string; cents: number; revenue: boolean | null }>
  revenue_cents: number | null
  non_revenue_cents: number | null
  /** Faux : une session n'a pas ses tickets — les comptes valent `null`, l'affluence et les produits sont partiels. */
  tickets_complete: boolean
  tickets: number | null
  item_numbers: number | null
  articles: number | null
  average_ticket_cents: number | null
  biggest_ticket_cents: number | null
  first_sale_at: string | null
  last_sale_at: string | null
  peak: Heure | null
  by_hour: Heure[]
  products: Produit[]
  doses: {
    rampe: string; total: number
    products: Array<{ product: string; doses: number; dose_cl: number | null; volume_l: number | null; sold_dosed: number | null }>
    bottle_changes: Array<{ at: string | null; product: string; doser: string }>
    incorrect_changes: Array<{ at: string | null; doser: string; doses: number | null }>
    matches_sales: boolean | null
  } | null
  drawer_openings: string[] | null
  offers: Offert[] | null
}

interface Guichet {
  events: Array<{ id: string; name: string; status: string }>
  tickets: number; facial_cents: number; free_tickets: number
  card_present_cents: number; cash_cents: number; other_cents: number; unknown_cents: number
  coverage: string
  by_rate: Array<{ name: string; tickets: number; facial_cents: number; unit_cents: number | null }>
  by_hour: Heure[]
  peak: Heure | null
  untimed_tickets: number
  late_tickets: number
  first_sale_at: string | null
  last_sale_at: string | null
  caveats: string[]
  /**
   * Les ventes du guichet faites pendant une AUTRE nuit que celle de leur
   * soirée, à part (08/10/2026) : tout ce qui précède ne compte que la nuit.
   * Absent d'un serveur d'avant : aucune.
   */
  off_night?: Array<{
    side: 'VENTE' | 'SOIREE'; other_business_date: string; from_event_name: string
    tickets: number; free_tickets: number; total_cents: number; card_present_cents: number; cash_cents: number
    first_ordered_at: string | null; last_ordered_at: string | null; status: string; sentence: string
  }>
}

interface Detail {
  business_date: string
  point_of_sale: { id: string; name: string; kind: string; location_name: string }
  summary: {
    revenue_cents: number | null; non_revenue_cents: number | null; tickets: number | null; articles: number | null
    /** Caisses dont TOUS les tickets sont lus, sur combien. */
    tickets_registers: { read: number; total: number }
    average_ticket_cents: number | null; peak: Heure | null; by_hour: Heure[]; products: Produit[]
    doses: number | null; drawer_openings: number | null; offers_count: number | null; offers_cents: number | null
  }
  registers: Caisse[]
  idle_registers: Array<{ register_id: string; register_name: string; has_zap_alias: boolean }>
  box_office: Guichet | null
  annexes: { caisses: number; doseurs: boolean; tiroirs: boolean; cartes: boolean }
  documents: Array<{ id: string; filename: string; kind: string }>
  /** Détails de caisse de la NUIT qu'une relecture compléterait (absent d'un serveur plus ancien : 0). */
  rereadable?: number
  /** Détails de caisse de la NUIT dont le fichier n'est plus sur le volume : à redéposer, rien à relire. */
  missing_files?: number
}

/** Le bilan d'une relecture des tickets (`relireAnnexesCaisseDeLaNuit`). */
export interface BilanRelecture {
  business_date: string; reports: number; reread: number; up_to_date: number; tickets: number; remaining: number
  unresolved: Array<{ document_id: string; filename: string; reason: string }>
}

const commeDetail = (v: unknown): Detail | null => {
  const o = typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : null
  return o && Array.isArray(o['registers']) && typeof o['summary'] === 'object' && o['summary'] !== null ? (o as unknown as Detail) : null
}

/* ═══════════════════════ petits outils d'écriture ═══════════════════════ */

/**
 * Un montant par `formatMoney`, insécable : « 474,00 € » ne se coupe jamais
 * entre le nombre et l'euro quand une phrase revient à la ligne.
 */
const montant = (c: number | null): string => formatMoney(c).replace(/ €$/, '\u00a0€')
const pluriel = (n: number, un: string, plusieurs = `${un}s`): string => `${n.toLocaleString('fr-FR')} ${n > 1 ? plusieurs : un}`
const nombre = (n: number): string => n.toLocaleString('fr-FR')
/** Un compte inconnu s'écrit « — », jamais 0. */
const compte = (n: number | null): string => (n === null ? '—' : nombre(n))
/**
 * Les messages venus du serveur (réserves, contrôles) écrivent « en échec :
 * Carte Bleue » avec une espace ordinaire : la ligne pouvait se couper AVANT
 * le deux-points et le laisser en tête de ligne. Espace insécable devant la
 * ponctuation double et à l'intérieur des guillemets, comme le veut l'usage.
 */
const insecable = (s: string): string => s.replace(/ ([:;!?»])/g, '\u00a0$1').replace(/« /g, '«\u00a0')
/** « 202610032243_Resultats - Caisse ….pdf » → le titre du rapport, sans horodatage ni extension. */
const titrePiece = (f: string): string => f.replace(/^\d{12}_/, '').replace(/\.pdf$/i, '')
const KIND_PIECE: Record<string, string> = { CAISSE: 'Détail de caisse', DOSEURS: 'Doseurs', TIROIRS: 'Tiroirs', CARTES: 'Cartes serveur' }

/** « Le détail de Vestiaire », « Le détail d’Accueil » : l'élision devant une voyelle. */
const titreDetail = (nom: string): string => (/^[aeiouyhàâäéèêëîïôöûüAEIOUYHÀÂÄÉÈÊËÎÏÔÖÛÜ]/.test(nom) ? `Le détail d’${nom}` : `Le détail de ${nom}`)

/**
 * La grille des chiffres : autant de colonnes que de tuiles quand la section
 * est large, sinon trois — deux si elles sont quatre, pour ne jamais laisser
 * une tuile seule à côté d'un trou (voir `.fpd-kpis` dans finance-pos.css).
 */
const colonnesKpis = (n: number): CSSProperties =>
  ({ '--fpd-kpis': n, '--fpd-kpis-moyen': n === 4 ? 2 : 3 }) as CSSProperties

type Mesure = 'montant' | 'tickets'
type Tri = 'qty' | 'montant' | 'nom'

/* ═══════════════════════════════ le composant ═══════════════════════════════ */

export function PosDetail(props: {
  nuit: string
  pointOfSaleId: string
  nom: string
  kind: string
  peut: (cle: string) => boolean
}) {
  const autorise = props.peut('finance.source.read')
  const [etat, setEtat] = useState<Etat<Detail>>({ etat: 'chargement' })
  const [portee, setPortee] = useState<string>('tout')
  const [mesure, setMesure] = useState<Mesure>('montant')
  const [vue, setVue] = useState<'barres' | 'tableau'>('barres')
  const [tri, setTri] = useState<Tri>('qty')
  const [q, setQ] = useState('')
  /* Le rechargement après une relecture : le bilan, lui, reste affiché. */
  const [version, setVersion] = useState(0)
  const [bilan, setBilan] = useState<BilanRelecture | null>(null)

  useEffect(() => {
    if (!autorise) return
    let vivant = true
    // Un rechargement après relecture garde la lecture précédente à l'écran :
    // pas de « chargement » qui ferait sauter toute la section.
    if (version === 0) setEtat({ etat: 'chargement' })
    void lireJson<Detail>(`/api/v1/finance/nights/${props.nuit}/pos/${props.pointOfSaleId}/detail`, commeDetail)
      .then((e) => { if (vivant) setEtat(e) })
    return () => { vivant = false }
  }, [props.nuit, props.pointOfSaleId, autorise, version])
  // Une autre nuit, un autre point de vente : l'ancien bilan ne les concerne pas.
  useEffect(() => { setBilan(null) }, [props.nuit, props.pointOfSaleId])

  const d = etat.etat === 'ok' ? etat.valeur : null
  const vestiaire = props.kind === 'VESTIAIRE'
  const caisse = d && portee !== 'tout' ? d.registers.find((r) => r.register_id === portee) ?? null : null
  const heures = caisse ? caisse.by_hour : d?.summary.by_hour ?? []
  const produits = caisse ? caisse.products : d?.summary.products ?? []
  const pieces: DocVisible[] = (d?.documents ?? []).map((p) => ({
    id: p.id, filename: p.filename, provider: 'ZAP_SIS', report_type: 'ZAP_OTHER', business_date: props.nuit,
    libelle: KIND_PIECE[p.kind] ?? null,
  }))
  const offerts = useMemo(
    () => (d?.registers ?? []).flatMap((r) => (r.offers ?? []).map((o) => ({ ...o, caisse: r.register_name }))).sort((a, b) => (a.at ?? '').localeCompare(b.at ?? '')),
    [d],
  )
  /* Les offerts de la caisse choisie : la liste, le test du vide ET le total
     de l'en-tête lisent la même sélection — un total de tout le point de
     vente au-dessus d'une liste filtrée ne retombait pas sur ses lignes. */
  const offertsVus = caisse === null ? offerts : offerts.filter((o) => o.caisse === caisse.register_name)
  const lus = d?.summary.tickets_registers ?? { read: 0, total: 0 }
  const partiel = lus.read < lus.total
  const offertsLisibles = (d?.registers ?? []).some((r) => r.offers !== null)
  const avecVentes = (d?.registers.length ?? 0) > 0
  const guichetVide = !d?.box_office || (d.box_office.tickets === 0 && d.box_office.events.length === 0)
  /* L'ACCUEIL n'a pas de caisse ZAP : ses ventes sont le guichet Shotgun et
     le comptage. Ses caisses sans libellé ZAP ne sont donc pas « sans
     rapport » — elles n'en auront jamais — et l'absence de détail de caisse
     n'y est pas un manque. On n'en parle que si une caisse ZAP y est
     rattachée un jour. */
  const billetterie = props.kind === 'BILLETTERIE'
  const inactives = (d?.idle_registers ?? []).filter((r) => !billetterie || r.has_zap_alias)
  const attendZap = !billetterie || (d?.idle_registers ?? []).some((r) => r.has_zap_alias)
  /* Le bouton « Lire les tickets de cette nuit » : là où des tickets manquent
     (ou rien n'est lu), et seulement si une relecture peut les apporter. */
  const aRelire = d?.rereadable ?? 0
  const proposerLecture = d !== null && aRelire > 0 && (partiel || (!avecVentes && attendZap))
  /* Un fichier qui a quitté le volume ne se relit pas : on le DIT, sans
     bouton ni promesse (« une lecture peut les apporter » était faux). */
  const sansFichier = d?.missing_files ?? 0
  const rampes = (d?.registers ?? []).filter((r) => r.doses !== null && (portee === 'tout' || r.register_id === portee))
  const nbCaisses = d?.registers.length ?? 1

  return (
    <section className="lc-panel fpd" aria-labelledby={`fpd-titre-${props.pointOfSaleId}`}>
      <div className="lc-panel__head">
        <span className="fih-head__icon" aria-hidden="true"><Receipt size={17} /></span>
        <h2 className="lc-panel__title fpd-tete__titre" id={`fpd-titre-${props.pointOfSaleId}`}>{titreDetail(props.nom)}</h2>
        <span className="lc-panel__spacer" />
        <span className="lc-hint fpd-tete__hint">À lire si vous voulez — rien ici ne change la clôture.</span>
      </div>

      <div className="lc-panel__body fpd-corps">
        {!autorise ? (
          <Indisponible titre="Le détail des ventes n’est pas accessible" message="Votre rôle ne donne pas accès aux documents source de la nuit : les tickets, produits et heures de vente en viennent." />
        ) : null}
        {autorise && etat.etat === 'chargement' ? <EnCours quoi="du détail des ventes" /> : null}
        {autorise && etat.etat === 'absent' ? <Indisponible titre="Le détail n’a pas pu être lu" message={etat.message} /> : null}

        {d && !avecVentes && guichetVide && attendZap ? (
          <div className="fi-empty">
            <div className="fi-empty__title">Aucun rapport de caisse lu pour ce point de vente cette nuit</div>
            <p>
              {aRelire > 0
                ? `${aRelire > 1 ? `${nombre(aRelire)} détails de caisse de cette nuit attendent leur lecture` : 'Un détail de caisse de cette nuit attend sa lecture'} : une fois lus, les tickets de ce point de vente apparaîtront ici s’ils en font partie.`
                : d.annexes.caisses === 0
                  ? 'Les détails de caisse ZAP de cette nuit ne sont pas encore arrivés : les tickets et les produits apparaîtront ici dès leur lecture.'
                  : 'Les détails de caisse ZAP reçus ne désignent aucune caisse de ce point de vente. Une caisse du rapport se rattache à une caisse de Gestion dans les référentiels.'}
            </p>
          </div>
        ) : null}

        {proposerLecture || bilan ? (
          <LireLesTickets nuit={props.nuit} aRelire={proposerLecture ? aRelire : 0} peutRelire={props.peut('finance.source.manage')}
            bilan={bilan} onBilan={(b) => { setBilan(b); setVersion((v) => v + 1) }} />
        ) : null}
        {d && sansFichier > 0 && (partiel || (!avecVentes && attendZap)) ? (
          <p className="fi-reserve fpd-reserve">
            {sansFichier > 1
              ? `${nombre(sansFichier)} détails de caisse de cette nuit n’ont plus leur fichier sur le volume d’archives : aucune lecture ne les apportera. Redéposez-les dans les sources reçues.`
              : 'Un détail de caisse de cette nuit n’a plus son fichier sur le volume d’archives : aucune lecture ne l’apportera. Redéposez-le dans les sources reçues.'}
          </p>
        ) : null}

        {d && avecVentes ? (
          <>
            {/* ═══════════ l'essentiel, en chiffres ═══════════ */}
            <div className="fpd-kpis" style={colonnesKpis(4 + (d.summary.doses !== null ? 1 : 0) + (d.summary.offers_count !== null ? 1 : 0))}>
              <Kpi icone={<Receipt size={15} />} label="Encaissé selon ZAP" valeur={montant(d.summary.revenue_cents)}
                sous={d.summary.non_revenue_cents !== null && d.summary.non_revenue_cents > 0 ? `et ${montant(d.summary.non_revenue_cents)} réglés par carte serveur` : 'carte bancaire et espèces, sans les offerts'} accent />
              <Kpi icone={<Ticket size={15} />} label={vestiaire ? 'Passages au comptoir' : 'Tickets'} valeur={compte(d.summary.tickets)}
                sous={d.summary.articles !== null
                  ? pluriel(d.summary.articles, 'article vendu', 'articles vendus')
                  : `tickets lus sur ${lus.read} caisse${lus.read > 1 ? 's' : ''} sur ${lus.total}`} />
              <Kpi icone={<BarChart3 size={15} />} label="Panier moyen" valeur={montant(d.summary.average_ticket_cents)} sous="par ticket payant" />
              <Kpi icone={<Clock size={15} />} label="Heure de pointe" valeur={d.summary.peak ? d.summary.peak.label : '—'}
                sous={d.summary.peak
                  ? `${pluriel(d.summary.peak.count, vestiaire ? 'passage' : 'ticket')} · ${montant(d.summary.peak.amount_cents)}`
                  : partiel ? 'inconnue tant qu’une caisse n’est pas lue' : 'aucune vente horodatée'} />
              {d.summary.doses !== null ? (
                <Kpi icone={<GlassWater size={15} />} label="Doses servies" valeur={nombre(d.summary.doses)} sous="par la rampe du bar" />
              ) : null}
              {d.summary.offers_count !== null ? (
                <Kpi icone={<Gift size={15} />} label="Offerts" valeur={montant(d.summary.offers_cents)} sous={pluriel(d.summary.offers_count, 'consommation offerte', 'consommations offertes')} />
              ) : null}
            </div>

            {partiel ? (
              <p className="fi-reserve fpd-reserve">
                <AlertTriangle size={14} aria-hidden="true" />{' '}
                {lus.read === 0
                  ? 'Les tickets d’aucune caisse ne sont lus cette nuit : les comptes restent inconnus, l’affluence et les produits ne peuvent pas être tracés. Le détail de chaque caisse dit pourquoi.'
                  : `Tickets lus sur ${lus.read} caisse${lus.read > 1 ? 's' : ''} sur ${lus.total} : les comptes du point de vente restent inconnus, l’affluence et les produits ne couvrent que les caisses lues.`}
              </p>
            ) : null}

            {/* ═══════════ une colonne par caisse ═══════════ */}
            <div className="fpd-caisses" style={{ '--fpd-caisses': nbCaisses } as CSSProperties}>
              {d.registers.map((r) => <CarteCaisse key={r.register_id} r={r} vestiaire={vestiaire} />)}
            </div>

            {/* ═══════════ la portée : tout le point de vente, ou une caisse ═══════════ */}
            {d.registers.length > 1 ? (
              <div className="fpd-portee">
                <span className="fpd-portee__label">Afficher</span>
                <div className="fpd-seg">
                  <SegmentedControl
                    value={portee}
                    options={[{ value: 'tout', label: 'Tout le point de vente' }, ...d.registers.map((r) => ({ value: r.register_id, label: r.register_name }))]}
                    onChange={setPortee}
                    ariaLabel="Caisse affichée dans l’affluence, les rampes et les produits"
                    size="sm"
                  />
                </div>
              </div>
            ) : null}

            {/* ═══════════ les rampes : sous leurs caisses, côte à côte ═══════════ */}
            {d.registers.some((r) => r.doses !== null) ? (
              <div className="fpd-bloc fpd-bloc--rampes">
                <div className="fpd-bloc__tete">
                  <h3 className="fpd-bloc__titre"><GlassWater size={16} aria-hidden="true" />Doses servies et bouteilles</h3>
                  <span className="fpd-bloc__sous">une colonne par caisse, la rampe du même nom</span>
                </div>
                {rampes.length === 0 ? (
                  <p className="lc-hint">Aucune rampe n’est relevée pour cette caisse.</p>
                ) : (
                  /* Même grille que les caisses, même ordre : la rampe d'une caisse
                     tombe SOUS sa carte. Une caisse sans rampe garde sa colonne,
                     sinon les suivantes glisseraient sous la mauvaise caisse. */
                  <div className="fpd-rampes" style={{ '--fpd-caisses': nbCaisses } as CSSProperties}>
                    {d.registers.filter((r) => portee === 'tout' || r.register_id === portee).map((r) => (r.doses !== null
                      ? <Rampe key={r.register_id} r={r} plusieurs={d.registers.length > 1} />
                      : (
                        <div key={r.register_id} className="fpd-rampe fpd-rampe--vide">
                          <span className="fpd-rampe__nom">{r.register_name}</span>
                          <p className="lc-hint">Aucune rampe du même nom n’est relevée pour cette caisse.</p>
                        </div>
                      )))}
                  </div>
                )}
              </div>
            ) : null}

            {/* ═══════════ l'analyse : deux ou trois colonnes selon la largeur ═══════════ */}
            <div className="fpd-analyse">
              <div className="fpd-bloc fpd-bloc--affluence">
                <div className="fpd-bloc__tete">
                  <h3 className="fpd-bloc__titre"><BarChart3 size={16} aria-hidden="true" />Affluence heure par heure</h3>
                  <div className="fpd-seg">
                    <SegmentedControl value={mesure} onChange={setMesure} size="sm" ariaLabel="Mesure de l’affluence"
                      options={[{ value: 'montant', label: 'Encaissé' }, { value: 'tickets', label: vestiaire ? 'Passages' : 'Tickets' }]} />
                    <SegmentedControl value={vue} onChange={setVue} size="sm" ariaLabel="Présentation de l’affluence"
                      options={[{ value: 'barres', label: 'Graphique' }, { value: 'tableau', label: 'Tableau' }]} />
                  </div>
                </div>
                <Affluence heures={heures} mesure={mesure} vue={vue} unite={vestiaire ? 'passage' : 'ticket'} libelleMontant="Encaissé" />
              </div>

              <div className="fpd-duo">
                <div className="fpd-bloc">
                  <div className="fpd-bloc__tete">
                    <h3 className="fpd-bloc__titre"><Vault size={16} aria-hidden="true" />Ouvertures de tiroir</h3>
                  </div>
                  {!d.annexes.tiroirs ? (
                    <p className="lc-hint">L’annexe ZAP des tiroirs-caisses n’est pas arrivée pour cette nuit.</p>
                  ) : (
                    <ul className="fpd-tiroirs">
                      {d.registers.filter((r) => portee === 'tout' || r.register_id === portee).map((r) => (
                        <li key={r.register_id}>
                          <span className="fpd-tiroirs__caisse">{r.register_name}</span>
                          <span className="fpd-tiroirs__nb">{r.drawer_openings === null ? '—' : pluriel(r.drawer_openings.length, 'ouverture')}</span>
                          <span className="fpd-tiroirs__heures">
                            {r.drawer_openings && r.drawer_openings.length > 0
                              ? r.drawer_openings.map((o) => heureParis(o)).join(' · ')
                              : 'L’annexe ne cite aucune ouverture pour cette caisse.'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="fpd-bloc">
                  <div className="fpd-bloc__tete">
                    <h3 className="fpd-bloc__titre"><Gift size={16} aria-hidden="true" />Offerts servis ici</h3>
                    {offertsLisibles && offertsVus.length > 0 ? (
                      <span className="fpd-bloc__total fi-money">{montant(offertsVus.reduce((s, o) => s + o.paid_cents + o.discount_cents, 0))}</span>
                    ) : null}
                  </div>
                  {!offertsLisibles ? (
                    <p className="lc-hint">
                      {d.annexes.cartes ? 'Votre rôle ne permet pas de lire les offerts.' : 'L’annexe ZAP des cartes serveur n’est pas arrivée pour cette nuit.'}
                    </p>
                  ) : offertsVus.length === 0 ? (
                    <p className="lc-hint">{caisse === null ? 'Aucune consommation offerte par carte serveur à ce point de vente.' : 'Aucune consommation offerte par carte serveur à cette caisse.'}</p>
                  ) : (
                    <ul className="fpd-offerts">
                      {offertsVus.map((o, i) => (
                        <li key={i}>
                          <span className="fpd-offerts__heure">{o.at ? heureParis(o.at) : '—'}</span>
                          <span className="fpd-offerts__quoi">
                            {o.articles.length > 0 ? o.articles.map((a) => `${a.qty} × ${a.product}`).join(', ') : 'Consommation sans détail d’article'}
                            <span className="fpd-offerts__qui">{o.holder}{d.registers.length > 1 ? ` · ${o.caisse}` : ''}</span>
                          </span>
                          <span className="fpd-offerts__montant fi-money">
                            {montant(o.paid_cents + o.discount_cents)}
                            <span className="fpd-offerts__type">{o.paid_cents > 0 && o.discount_cents > 0 ? 'offert et remise' : o.discount_cents > 0 ? 'remise' : 'offert'}</span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* ─── tous les produits ─── */}
              <div className="fpd-bloc fpd-bloc--produits">
                <div className="fpd-bloc__tete">
                  <h3 className="fpd-bloc__titre"><Package size={16} aria-hidden="true" />Produits vendus</h3>
                  <span className="fpd-bloc__sous">{pluriel(produits.length, 'référence')} · {pluriel(produits.reduce((s, p) => s + p.qty, 0), 'article')}</span>
                </div>
                <div className="fpd-filtres">
                  <label className="fpd-recherche">
                    <Search size={15} aria-hidden="true" />
                    <input className="lc-input" placeholder="Chercher un produit" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Chercher un produit" />
                  </label>
                  <div className="fpd-seg">
                    <SegmentedControl value={tri} onChange={setTri} size="sm" ariaLabel="Ordre des produits"
                      options={[{ value: 'qty', label: 'Quantité' }, { value: 'montant', label: 'Montant' }, { value: 'nom', label: 'Nom' }]} />
                  </div>
                </div>
                <ListeProduits key={`${portee}-${tri}`} produits={produits} tri={tri} q={q} />
              </div>
            </div>
          </>
        ) : null}

        {d && inactives.length > 0 && (avecVentes || !guichetVide) ? (
          <ul className="fpd-inactives" aria-label="Caisses sans rapport cette nuit">
            {inactives.map((r) => (
              <li key={r.register_id}>
                <span className="fpd-inactives__nom">{r.register_name}</span>
                <span className="fpd-inactives__pourquoi">
                  {r.has_zap_alias
                    ? 'aucun rapport ZAP cette nuit : caisse restée fermée, ou rapport pas encore arrivé'
                    : 'aucune caisse ZAP n’y est rattachée : ses ventes ne peuvent pas apparaître ici'}
                </span>
              </li>
            ))}
          </ul>
        ) : null}

        {d && d.box_office ? <GuichetShotgun g={d.box_office} separe={avecVentes} /> : null}
        {d && !d.box_office && props.kind === 'BILLETTERIE' ? (
          <p className="lc-hint fpd-note">Le guichet Shotgun n’est pas affiché : votre rôle ne donne pas accès aux ventes en direct.</p>
        ) : null}

        {pieces.length > 0 ? (
          <div className="fpd-pieces">
            <span className="lc-hint">Pièces lues :</span>
            {pieces.map((p) => (
              <VoirSource key={p.id} doc={p} liste={pieces} className="fiw-piece">{titrePiece(p.filename)}</VoirSource>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  )
}

/* ═══════════════════════════════ lire les tickets de la nuit ═══════════════════════════════ */

/**
 * LE GESTE QUI MANQUAIT — relire les détails de caisse de la nuit. Le bouton
 * n'existe que pour qui gère les sources ; les autres lisent qui peut le
 * faire. Le bilan dit ce qui a été lu et, rapport par rapport, ce qui reste
 * sans tickets et pourquoi (une bande que ZAP a mal imprimée ne se répare pas
 * en la relisant).
 */
export function LireLesTickets(props: {
  nuit: string
  /** Détails de caisse à relire ; 0 : seul le bilan s'affiche. */
  aRelire: number
  peutRelire: boolean
  bilan: BilanRelecture | null
  onBilan: (b: BilanRelecture) => void
  /** Variante en ligne, pour une liste (la couverture de Ventes & produits). */
  compact?: boolean
}) {
  const [enCours, setEnCours] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)

  const lire = async (): Promise<void> => {
    setEnCours(true)
    setErreur(null)
    try {
      const r = await adminMutate<Partial<BilanRelecture> & { error?: string; message?: string }>(
        'POST', `/api/v1/finance/nights/${props.nuit}/annexes/reread`)
      if (r.status === 200 && typeof r.data.reports === 'number') props.onBilan(r.data as BilanRelecture)
      else setErreur(r.data.message ?? 'La lecture des tickets n’a pas pu être lancée.')
    } catch {
      setErreur('Gestion n’a pas pu joindre le serveur. Réessayez dans un instant.')
    } finally {
      setEnCours(false)
    }
  }

  const b = props.bilan
  return (
    <div className={`fpd-relire${props.compact ? ' fpd-relire--compact' : ''}${b && b.unresolved.length === 0 && props.aRelire === 0 ? ' fpd-relire--fait' : ''}`} aria-live="polite">
      {props.aRelire > 0 ? (
        <div className="fpd-relire__ligne">
          <p className="fpd-relire__texte">
            <AlertTriangle size={15} aria-hidden="true" />
            <span>
              {b && b.reread > 0
                ? `${props.aRelire > 1 ? `${nombre(props.aRelire)} détails de caisse restent` : 'Un détail de caisse reste'} à lire pour cette nuit.`
                : props.aRelire > 1
                  ? `${nombre(props.aRelire)} détails de caisse de cette nuit n’ont pas encore donné leurs tickets : une lecture peut les apporter.`
                  : 'Un détail de caisse de cette nuit n’a pas encore donné ses tickets : une lecture peut les apporter.'}
              {' '}La clôture n’en est pas modifiée.
            </span>
          </p>
          {props.peutRelire ? (
            <button type="button" className="lc-btn lc-btn--primary lc-btn--sm fpd-relire__bouton" onClick={() => void lire()} disabled={enCours} aria-busy={enCours}>
              <RefreshCw size={15} aria-hidden="true" className={enCours ? 'fpd-relire__tourne' : undefined} />
              {enCours ? 'Lecture des tickets en cours' : 'Lire les tickets de cette nuit'}
            </button>
          ) : (
            <span className="lc-hint fpd-relire__droit">Une personne qui gère les sources reçues peut lancer cette lecture depuis cet écran.</span>
          )}
        </div>
      ) : null}
      {erreur ? <p className="fi-reserve fpd-reserve">{insecable(erreur)}</p> : null}
      {b ? (
        <div className="fpd-relire__bilan">
          <p className="fpd-relire__resume">
            {b.unresolved.length === 0 ? <CheckCircle2 size={15} aria-hidden="true" /> : <AlertTriangle size={15} aria-hidden="true" />}
            <span>
              {b.reread === 0
                ? 'Rien à relire : les détails de caisse de cette nuit sont déjà lus par la lecture actuelle.'
                : `${pluriel(b.reread, 'détail de caisse relu', 'détails de caisse relus')} : ${pluriel(b.tickets, 'ticket')} en base pour la nuit, ${b.up_to_date} rapport${b.up_to_date > 1 ? 's' : ''} sur ${b.reports} à jour.`}
            </span>
          </p>
          {b.unresolved.length > 0 ? (
            <ul className="fpd-relire__restes">
              {b.unresolved.map((u) => (
                <li key={u.document_id}>
                  <span className="fpd-relire__piece">{titrePiece(u.filename)}</span>
                  <span className="fpd-relire__raison">{insecable(u.reason)}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/* ═══════════════════════════════ les morceaux ═══════════════════════════════ */

function Kpi(props: { icone: ReactNode; label: string; valeur: string; sous: string; accent?: boolean }) {
  return (
    <div className={`fpd-kpi${props.accent ? ' fpd-kpi--accent' : ''}`}>
      <span className="fpd-kpi__label"><span aria-hidden="true">{props.icone}</span>{props.label}</span>
      <span className="fpd-kpi__valeur">{props.valeur}</span>
      <span className="fpd-kpi__sous">{props.sous}</span>
    </div>
  )
}

/** Une caisse : ses moyens, ses chiffres, sa nuit en petit, ses sessions et ses réserves. */
function CarteCaisse(props: { r: Caisse; vestiaire: boolean }) {
  const { r, vestiaire } = props
  const max = Math.max(1, ...r.by_hour.map((h) => h.amount_cents))
  return (
    <article className="fpd-caisse">
      <header className="fpd-caisse__tete">
        <div className="fpd-caisse__ident">
          <h3 className="fpd-caisse__nom">{r.register_name}</h3>
          <p className="fpd-caisse__alias">Libellé du rapport : {r.zap_labels.join(' · ')}</p>
        </div>
        <div className="fpd-caisse__total">
          <span className="fpd-caisse__total-val">{montant(r.revenue_cents)}</span>
          <span className="fpd-caisse__total-label">encaissé</span>
        </div>
      </header>

      {r.by_method.length > 0 ? (
        <ul className="fpd-moyens">
          {r.by_method.map((m) => (
            <li key={m.label} className={m.revenue === false ? 'fpd-moyens__hors' : undefined}>
              <span className="fpd-moyens__label">
                {m.label}
                {m.revenue === false ? <span className="fpd-moyens__tag">offerts, hors encaissé</span> : null}
              </span>
              <span className="fpd-moyens__val fi-money">{montant(m.cents)}</span>
            </li>
          ))}
        </ul>
      ) : null}

      <dl className="fpd-faits">
        <div><dt>{vestiaire ? 'Passages' : 'Tickets'}</dt><dd>{compte(r.tickets)}</dd></div>
        {vestiaire ? <div><dt>Numéros remis</dt><dd>{compte(r.item_numbers)}</dd></div> : <div><dt>Articles</dt><dd>{compte(r.articles)}</dd></div>}
        <div><dt>Panier moyen</dt><dd>{montant(r.average_ticket_cents)}</dd></div>
        <div><dt>Heure de pointe</dt><dd>{r.peak ? `${r.peak.label} · ${pluriel(r.peak.count, vestiaire ? 'passage' : 'ticket')}` : '—'}</dd></div>
        <div><dt>Plus gros ticket encaissé</dt><dd>{montant(r.biggest_ticket_cents)}</dd></div>
        <div><dt>Première et dernière vente</dt><dd>{r.first_sale_at && r.last_sale_at ? `${heureParis(r.first_sale_at)} → ${heureParis(r.last_sale_at)}` : '—'}</dd></div>
      </dl>

      {r.by_hour.length > 0 ? (
        <div className="fpd-mini" aria-hidden="true">
          {r.by_hour.map((h) => (
            <span key={h.hour_start} className="fpd-mini__col">
              <span className="fpd-mini__barre" style={{ height: `${Math.round((h.amount_cents / max) * 100)}%` }} />
            </span>
          ))}
        </div>
      ) : null}

      <div className="fpd-sessions">
        {r.sessions.map((s) => (
          <span key={s.document_id} className={`lc-pill ${s.offline ? 'lc-pill--warning' : 'lc-pill--idle'}`}>
            {s.offline ? 'Complément hors ligne' : 'Session'}{s.session_no !== null ? ` n° ${s.session_no}` : ''}
            {s.opened_at && s.closed_at ? ` · ${heureParis(s.opened_at)} → ${heureParis(s.closed_at)}` : ''}
          </span>
        ))}
      </div>
      {r.sessions.filter((s) => s.problem).map((s) => (
        <p key={`p-${s.document_id}`} className="fi-reserve fpd-reserve"><AlertTriangle size={14} aria-hidden="true" /> {insecable(s.problem ?? '')}</p>
      ))}
      {r.sessions.flatMap((s) => s.warnings.map((w, i) => (
        <p key={`w-${s.document_id}-${i}`} className="fi-reserve fpd-reserve">{insecable(w)}</p>
      )))}
    </article>
  )
}

/**
 * L'AFFLUENCE — une barre par heure, de la première à la dernière vente.
 * Une seule série (le titre la nomme : pas de légende), la valeur de l'heure
 * de pointe écrite sur sa barre, le reste au survol ou au clavier, et le
 * tableau pour tout lire sans viser.
 */
function Affluence(props: { heures: Heure[]; mesure: Mesure; vue: 'barres' | 'tableau'; unite: string; libelleMontant: string }) {
  const { heures, mesure, unite } = props
  const [vise, setVise] = useState<number | null>(null)
  const valeur = (h: Heure): number => (mesure === 'montant' ? h.amount_cents : h.count)
  const ecrire = (h: Heure): string => (mesure === 'montant' ? montant(h.amount_cents) : pluriel(h.count, unite))
  const max = Math.max(0, ...heures.map(valeur))
  const pointe = heures.reduce<number | null>((best, h, i) => (valeur(h) > 0 && (best === null || valeur(h) > valeur(heures[best]!)) ? i : best), null)
  if (heures.length === 0) return <p className="lc-hint">Aucune vente horodatée : l’affluence ne peut pas être tracée.</p>

  if (props.vue === 'tableau') {
    return (
      <div className="fi-tablewrap">
        <table className="fi-table fpd-table">
          <thead>
            <tr><th scope="col">Heure</th><th scope="col" className="fi-money">{unite.charAt(0).toUpperCase()}{unite.slice(1)}s</th><th scope="col" className="fi-money">{props.libelleMontant}</th></tr>
          </thead>
          <tbody>
            {heures.map((h) => (
              <tr key={h.hour_start}>
                <th scope="row">{h.label}</th>
                <td className="fi-money">{nombre(h.count)}</td>
                <td className="fi-money">{montant(h.amount_cents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  const lue = vise !== null ? heures[vise] ?? null : pointe !== null ? heures[pointe]! : null
  return (
    <div className="fpd-hist">
      <p className="fpd-hist__lecture" aria-live="polite">
        {lue ? (
          <>
            <strong>{lue.label}</strong>
            <span>{pluriel(lue.count, unite)} · {montant(lue.amount_cents)}</span>
            {vise === null ? <span className="fpd-hist__aide">heure de pointe — survolez ou touchez une barre pour lire les autres</span> : null}
          </>
        ) : <span className="fpd-hist__aide">Aucune vente sur la période.</span>}
      </p>
      <div className="fpd-hist__plot" style={{ gridTemplateColumns: `repeat(${heures.length}, minmax(0, 1fr))` }} onMouseLeave={() => setVise(null)}>
        {heures.map((h, i) => {
          const v = valeur(h)
          const pct = max === 0 ? 0 : Math.max(v > 0 ? 2 : 0, Math.round((v / max) * 100))
          return (
            <button
              key={h.hour_start}
              type="button"
              className={`fpd-hist__col${vise === i ? ' fpd-hist__col--vise' : ''}`}
              aria-label={`${h.label} : ${pluriel(h.count, unite)}, ${montant(h.amount_cents)}`}
              onMouseEnter={() => setVise(i)}
              onFocus={() => setVise(i)}
              onBlur={() => setVise(null)}
            >
              {i === pointe ? (
                <span
                  className={`fpd-hist__valeur${i === 0 && heures.length > 1 ? ' fpd-hist__valeur--debut' : i === heures.length - 1 && heures.length > 1 ? ' fpd-hist__valeur--fin' : ''}`}
                  style={{ bottom: `${pct}%` }}
                >{ecrire(h)}</span>
              ) : null}
              <span className="fpd-hist__barre" style={{ height: `${pct}%` }} />
            </button>
          )
        })}
      </div>
      <div className="fpd-hist__axe" style={{ gridTemplateColumns: `repeat(${heures.length}, minmax(0, 1fr))` }} aria-hidden="true">
        {heures.map((h, i) => <span key={h.hour_start} className={`fpd-hist__tick${i % 2 === 1 ? ' fpd-hist__tick--pair' : ''}`}>{h.label}</span>)}
      </div>
    </div>
  )
}

/** Les produits montrés d'emblée : la liste entière se déplie d'un clic, ou par la recherche. */
const PREMIERS_PRODUITS = 15

/** Tous les produits, triés, cherchables, avec une barre proportionnelle à la mesure du tri. */
function ListeProduits(props: { produits: Produit[]; tri: Tri; q: string }) {
  const [tous, setTous] = useState(false)
  const cherche = props.q.trim().toLocaleLowerCase('fr')
  const valeurDe = (p: Produit): number => p.revenue_cents ?? p.gross_cents ?? 0
  const tries = useMemo(() => {
    const l = [...props.produits]
    if (props.tri === 'montant') l.sort((a, b) => valeurDe(b) - valeurDe(a) || b.qty - a.qty)
    else if (props.tri === 'nom') l.sort((a, b) => a.product.localeCompare(b.product, 'fr'))
    else l.sort((a, b) => b.qty - a.qty || valeurDe(b) - valeurDe(a))
    return l
  }, [props.produits, props.tri])
  // Le rang est celui de la nuit, pas celui du filtre : chercher « shooter » ne fait pas de lui le n° 1.
  const rang = new Map(tries.map((p, i) => [p.product, i + 1]))
  const filtres = tries.filter((p) => cherche === '' || p.product.toLocaleLowerCase('fr').includes(cherche))
  const visibles = tous || cherche !== '' ? filtres : filtres.slice(0, PREMIERS_PRODUITS)
  const max = Math.max(1, ...props.produits.map((p) => (props.tri === 'montant' ? valeurDe(p) : p.qty)))

  if (props.produits.length === 0) return <p className="lc-hint">Aucun produit relevé pour cette sélection.</p>
  if (filtres.length === 0) return <p className="lc-hint">Aucun produit ne contient « {props.q.trim()} ».</p>
  return (
    <>
    <ol className="fpd-produits">
      {visibles.map((p) => {
        const r = rang.get(p.product) ?? 0
        const part = props.tri === 'montant' ? valeurDe(p) : p.qty
        const details = [
          p.unit_prices_cents.length > 0 ? `${p.unit_prices_cents.map((c) => montant(c)).join(' puis ')} l’unité` : null,
          p.dosed_qty > 0 ? `${nombre(p.dosed_qty)} au doseur` : null,
          p.non_revenue_cents !== null && p.non_revenue_cents > 0 ? `${montant(p.non_revenue_cents)} offerts` : null,
        ].filter((x): x is string => x !== null)
        return (
          <li key={p.product} className={`fpd-produit${props.tri !== 'nom' && r <= 3 ? ' fpd-produit--podium' : ''}`}>
            <span className="fpd-produit__rang" aria-label={`rang ${r}`}>{props.tri === 'nom' ? '' : r}</span>
            <span className="fpd-produit__nom">
              {p.product}
              {details.length > 0 ? <span className="fpd-produit__sous">{details.join(' · ')}</span> : null}
            </span>
            <span className="fpd-produit__piste" aria-hidden="true">
              <span className="fpd-produit__barre" style={{ width: `${Math.max(2, Math.round((part / max) * 100))}%` }} />
            </span>
            <span className="fpd-produit__qte">{nombre(p.qty)}</span>
            <span className="fpd-produit__montant fi-money">{montant(p.revenue_cents)}</span>
          </li>
        )
      })}
    </ol>
    {visibles.length < filtres.length ? (
      <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm fpd-plus" onClick={() => setTous(true)}>
        Afficher les {pluriel(filtres.length - visibles.length, 'autre produit', 'autres produits')}
      </button>
    ) : null}
    </>
  )
}

/** La rampe du bar : doses servies, concordance avec les ventes « au doseur », bouteilles changées. */
function Rampe(props: { r: Caisse; plusieurs: boolean }) {
  const dz = props.r.doses!
  const max = Math.max(1, ...dz.products.map((p) => p.doses))
  return (
    <div className="fpd-rampe">
      <div className="fpd-rampe__tete">
        <span className="fpd-rampe__nom">
          {props.plusieurs ? `${props.r.register_name} · ` : ''}{pluriel(dz.total, 'dose servie', 'doses servies')}
          <span className="fpd-caisse__alias">Libellé du rapport : {dz.rampe}</span>
        </span>
        {dz.matches_sales === true ? (
          <span className="lc-pill lc-pill--ok"><CheckCircle2 size={14} aria-hidden="true" />Concorde avec les ventes au doseur</span>
        ) : dz.matches_sales === false ? (
          <span className="lc-pill lc-pill--warning"><AlertTriangle size={14} aria-hidden="true" />Écart avec les ventes au doseur</span>
        ) : null}
      </div>
      <ul className="fpd-doses">
        {dz.products.map((p) => (
          <li key={p.product}>
            <span className="fpd-doses__nom">
              {p.product}
              {p.sold_dosed !== null && p.sold_dosed !== p.doses
                ? <span className="fpd-doses__ecart">{pluriel(p.sold_dosed, 'vente marquée', 'ventes marquées')} « doseur »</span>
                : null}
            </span>
            <span className="fpd-doses__piste" aria-hidden="true"><span className="fpd-doses__barre" style={{ width: `${Math.round((p.doses / max) * 100)}%` }} /></span>
            <span className="fpd-doses__val">
              {nombre(p.doses)}
              {p.dose_cl !== null ? <span className="fpd-doses__unite"> × {p.dose_cl.toLocaleString('fr-FR')} cl</span> : null}
            </span>
          </li>
        ))}
      </ul>
      {dz.bottle_changes.length > 0 ? (
        <div className="fpd-bouteilles">
          <span className="fpd-bouteilles__titre">{pluriel(dz.bottle_changes.length, 'bouteille changée', 'bouteilles changées')}</span>
          <ul className="fpd-frise">
            {dz.bottle_changes.map((b, i) => (
              <li key={i}><span className="fpd-frise__heure">{b.at ? heureParis(b.at) : '—'}</span><span>{b.product} · {b.doser.toLowerCase()}</span></li>
            ))}
          </ul>
        </div>
      ) : <p className="lc-hint">Aucun changement de bouteille enregistré sur cette rampe.</p>}
      {dz.incorrect_changes.length > 0 ? (
        <details className="fpd-incorrects">
          <summary><AlertTriangle size={14} aria-hidden="true" />{pluriel(dz.incorrect_changes.length, 'changement signalé', 'changements signalés')} « incorrect » par ZAP</summary>
          <p className="lc-hint">Le nombre est celui des doses servies entre deux changements : un nombre bas peut signaler une bouteille changée trop tôt.</p>
          <ul className="fpd-frise">
            {dz.incorrect_changes.map((b, i) => (
              <li key={i}><span className="fpd-frise__heure">{b.at ? heureParis(b.at) : '—'}</span><span>{b.doser.toLowerCase()} · {b.doses === null ? 'doses inconnues' : pluriel(b.doses, 'dose')}</span></li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  )
}

/**
 * LE GUICHET SHOTGUN — la vente de billets à l'entrée, en VALEUR FACIALE (le
 * prix affiché sur les billets, pas un montant encaissé). Elle se COMPARE au
 * comptage de l'accueil, elle ne s'y ajoute jamais.
 */
function GuichetShotgun(props: { g: Guichet; separe: boolean }) {
  const { g } = props
  const [mesure, setMesure] = useState<Mesure>('tickets')
  const [vue, setVue] = useState<'barres' | 'tableau'>('barres')
  return (
    <div className={`fpd-guichet${props.separe ? ' fpd-guichet--separe' : ''}`}>
      <div className="fpd-bloc__tete">
        <h3 className="fpd-bloc__titre"><Ticket size={16} aria-hidden="true" />Le guichet Shotgun</h3>
        {g.events.length > 0 ? <span className="fpd-bloc__sous">{g.events.map((e) => e.name).join(' · ')}</span> : null}
      </div>
      {g.events.length === 0 || g.tickets === 0 ? (
        <p className="lc-hint">
          {g.events.length === 0
            ? g.caveats[0] ?? 'Aucune soirée n’est rattachée à cette nuit : il n’y a pas de guichet à lire.'
            : 'Aucun billet vendu sur place cette nuit : Shotgun ne relève aucune vente au guichet.'}
        </p>
      ) : (
        <>
          <div className="fpd-kpis" style={colonnesKpis(4 + (g.other_cents > 0 ? 1 : 0) + (g.unknown_cents > 0 ? 1 : 0))}>
            <Kpi icone={<Ticket size={15} />} label="Billets vendus sur place" valeur={nombre(g.tickets)}
              sous={g.free_tickets > 0 ? `dont ${pluriel(g.free_tickets, 'billet exonéré', 'billets exonérés')}` : 'tous payants'} />
            <Kpi icone={<Receipt size={15} />} label="Valeur faciale" valeur={montant(g.facial_cents)} sous="prix affiché sur les billets" accent />
            <Kpi icone={<CreditCard size={15} />} label="Par carte au TPE" valeur={montant(g.card_present_cents)} sous="à retrouver au TPE de l’accueil" />
            <Kpi icone={<Banknote size={15} />} label="En espèces" valeur={montant(g.cash_cents)} sous="à retrouver au comptage" />
            {g.other_cents > 0 ? <Kpi icone={<CreditCard size={15} />} label="Autres moyens" valeur={montant(g.other_cents)} sous="carte en ligne, portefeuille" /> : null}
            {g.unknown_cents > 0 ? <Kpi icone={<AlertTriangle size={15} />} label="Moyen non relevé" valeur={montant(g.unknown_cents)} sous="compté dans le total, dans aucune case" /> : null}
          </div>
          <div className="fpd-analyse fpd-analyse--guichet">
            <div className="fpd-bloc">
              <div className="fpd-bloc__tete">
                <h4 className="fpd-bloc__titre">Par tarif</h4>
              </div>
              <div className="fi-tablewrap">
                <table className="fi-table fpd-table">
                  <thead>
                    <tr><th scope="col">Tarif</th><th scope="col" className="fi-money">Billets</th><th scope="col" className="fi-money fpd-col-prix">Prix</th><th scope="col" className="fi-money">Valeur faciale</th></tr>
                  </thead>
                  <tbody>
                    {g.by_rate.map((t) => (
                      <tr key={t.name}>
                        <th scope="row"><span className="fi-cell-titre">{t.name}</span></th>
                        <td className="fi-money">{nombre(t.tickets)}</td>
                        <td className="fi-money fpd-col-prix">{t.unit_cents === null ? 'plusieurs prix' : montant(t.unit_cents)}</td>
                        <td className="fi-money fi-money--fort">{montant(t.facial_cents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="fpd-bloc">
              <div className="fpd-bloc__tete">
                <h4 className="fpd-bloc__titre">Arrivées au guichet, heure par heure</h4>
                <div className="fpd-seg">
                  <SegmentedControl value={mesure} onChange={setMesure} size="sm" ariaLabel="Mesure du guichet"
                    options={[{ value: 'tickets', label: 'Billets' }, { value: 'montant', label: 'Valeur faciale' }]} />
                  <SegmentedControl value={vue} onChange={setVue} size="sm" ariaLabel="Présentation du guichet"
                    options={[{ value: 'barres', label: 'Graphique' }, { value: 'tableau', label: 'Tableau' }]} />
                </div>
              </div>
              <Affluence heures={g.by_hour} mesure={mesure} vue={vue} unite="billet" libelleMontant="Valeur faciale" />
              {g.untimed_tickets > 0 ? <p className="lc-hint">{pluriel(g.untimed_tickets, 'billet')} sans heure relevée : hors du graphique, compté dans le total.</p> : null}
            </div>
          </div>
          {g.caveats.length > 0 ? (
            <ul className="fpd-reserves">
              {g.caveats.map((c) => <li key={c} className="fi-reserve">{insecable(c)}</li>)}
            </ul>
          ) : null}
        </>
      )}
      {(g.off_night ?? []).length > 0 ? <GuichetHorsNuitDetail cas={g.off_night!} /> : null}
    </div>
  )
}

/**
 * LES VENTES DU GUICHET D'UNE AUTRE NUIT, À PART — le guichet ci-dessus ne
 * compte que les billets vendus PENDANT la nuit (la soirée du 26/09/2026
 * étalait son affluence sur 174 heures, jusqu'aux 110 billets vendus le 03/10
 * sur la caisse restée ouverte). Ici : ce qui a été vendu, quand, combien, et
 * l'état de la décision ; la décision se prend à l'étape Accueil.
 */
function GuichetHorsNuitDetail(props: { cas: NonNullable<Guichet['off_night']> }) {
  return (
    <div className="fpd-bloc">
      <div className="fpd-bloc__tete">
        <h4 className="fpd-bloc__titre"><AlertTriangle size={15} aria-hidden="true" />Ventes du guichet hors de leur nuit</h4>
        <span className="fpd-bloc__sous">comptées à part : la décision se prend à l’étape Accueil</span>
      </div>
      <div className="fi-tablewrap">
        <table className="fi-table fpd-table">
          <thead>
            <tr>
              <th scope="col">Ce qui s’est passé</th>
              <th scope="col">Vendus</th>
              <th scope="col" className="fi-money">Billets</th>
              <th scope="col" className="fi-money">Valeur faciale</th>
            </tr>
          </thead>
          <tbody>
            {props.cas.map((c) => (
              <tr key={`${c.side}-${c.other_business_date}-${c.from_event_name}`}>
                <th scope="row">
                  <span className="fi-cell-titre">{c.sentence}</span>
                </th>
                <td>{c.first_ordered_at && c.last_ordered_at ? `de ${heureParis(c.first_ordered_at)} à ${heureParis(c.last_ordered_at)}` : '—'}</td>
                <td className="fi-money">
                  {nombre(c.tickets)}
                  {c.free_tickets > 0 ? <span className="fi-cell-sous">dont {pluriel(c.free_tickets, 'exonéré', 'exonérés')}</span> : null}
                </td>
                <td className="fi-money fi-money--fort">
                  {montant(c.total_cents)}
                  <span className="fi-cell-sous">TPE {montant(c.card_present_cents)} · espèces {montant(c.cash_cents)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
